// src/components/ClassCommentsV2.js — the class comment thread with member
// replies + moderation. UX: docs/ux/member-replies.md (APPROVED 2026-10-05).
// ADR: docs/decisions/adr-member-replies-threads-and-moderation.md.
//
// Rendered by ClassComments.js ONLY when siteConfig.memberRepliesEnabled is on
// (or a member previews it); otherwise the legacy thread renders unchanged.
//
// What's different from the legacy thread:
//   • Anyone who can comment can reply to anyone (one level, @Name for
//     reply-to-a-reply). Members post through /api/post-comment (server sets
//     identity, blocks links, AI-screens before others see it). Danielle keeps
//     her existing client path, notifications and Reply Assistant integration.
//   • Top-level comments page independently of replies, and replies for the
//     visible comments load alongside — no backfill/preservation patches needed,
//     and "View N more replies" expands with zero network.
//   • Members can delete their own comments (6s Undo) and report others' (hidden
//     for them at once; 2 distinct reports hide it for everyone).
//   • Held comments: the author sees hers with an honest note; Danielle sees a
//     dashed "Hidden · review" outline; nobody else sees it, and it's not counted.
//
// All sub-components are MODULE-SCOPE (an inner component remounts on every
// render and drops input focus — see feedback_no_inner_components). Every
// floating surface is portalled to <body> (PHOTO-001 framer trap) and stops
// event propagation so taps never reach a host modal's backdrop.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as Sentry from '@sentry/react';
import AvatarPhoto from './AvatarPhoto';
import MemberCardTrigger from './MemberCardTrigger';
import { useMemberPhotos } from '../services/memberProfiles';
import {
    postComment, deleteComment, editComment,
    subscribeTopLevel, subscribeRepliesFor, postMemberComment, deleteOwnComment, reportComment, fetchMyReportedIds,
} from '../services/classComments';
import { notifyCoachReply, removeCoachReplyNotification, notifyNewComment } from '../services/commentNotifications';
import { markDraftAnswered } from '../services/replyAssistant';
import { getCachedSiteContent } from '../services/firebase';
import { hasLink } from '../utils/linkDetect';

// ── Theme-safe tones (production warm-gray == charcoal; cream-dark == cream) ──
const SOFT = 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))';
const FAINT = 'color-mix(in srgb, var(--mom-charcoal) 52%, var(--mom-cream))';
const HAIR = 'color-mix(in srgb, var(--mom-charcoal) 9%, #fff)';
const SANS = 'var(--mom-font-sans)';
const SERIF = 'var(--mom-font-serif)';

const PAGE = 20;
const ADMIN_PAGE = 50;
const SHOW_REPLIES = 2;   // UX §4.1 (owner: 2)
const UNDO_MS = 6000;
const MAX = 280;

const DEFAULT_COPY = {
    commentReplyLabel: 'Reply',
    commentReplyingTo: 'Replying to {name}',
    commentReplyPlaceholder: 'Write a reply…',
    commentViewMoreReplies: 'View {count} more replies',
    commentHideReplies: 'Hide replies',
    commentLinkBlocked: "Links can't be shared in comments. Remove it to post.",
    commentDeletedUndo: 'Comment deleted',
    commentDeleteWithReplies: 'This will also remove {count} replies.',
    commentReportTitle: 'Report this comment?',
    commentReportSubtext: "Danielle's team will take a look.",
    commentReportReason1: 'Unkind or hurtful',
    commentReportReason2: 'Spam or selling',
    commentReportReason3: 'Something else',
    commentReportThanks: 'Thanks for letting us know.',
    commentReportedPlaceholder: 'You reported this comment',
    commentHeldNote: "Only visible to you while it's reviewed.",
};

function timeAgo(isoString) {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
// ── Session cache (UX §8): a return visit paints the last-seen thread at once,
// then the live listeners refresh it in place. Per browser tab session only;
// skipped silently when storage is unavailable or the thread is very large.
const CACHE_PREFIX = 'mom_ct_v1:';
const CACHE_MAX_BYTES = 200000;
function readThreadCache(key) {
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + key);
        if (!raw) return null;
        const c = JSON.parse(raw);
        return c && Array.isArray(c.top) && Array.isArray(c.replies) ? c : null;
    } catch (_) { return null; }
}
function writeThreadCache(key, top, replies) {
    if (!key) return;
    try {
        const raw = JSON.stringify({ t: Date.now(), top, replies });
        if (raw.length <= CACHE_MAX_BYTES) sessionStorage.setItem(CACHE_PREFIX + key, raw);
    } catch (_) { /* quota / private mode — the thread just loads normally */ }
}

const fill = (tpl, map) => Object.keys(map).reduce((s, k) => s.split(`{${k}}`).join(String(map[k])), tpl || '');
const isTemp = (c) => String(c.id).startsWith('temp-');
const stop = (e) => e.stopPropagation();

// "@Nicole R." at the start of a reply renders as a soft mention.
function ReplyText({ text, isReply }) {
    const m = /^@([^\s]+(?:\s[^\s]{1,3}\.?)?)\s/.exec(text || '');
    const style = { fontFamily: SANS, fontSize: isReply ? 13 : 14, color: 'var(--mom-charcoal)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 };
    if (!m) return <p style={style}>{text}</p>;
    return <p style={style}><span style={{ fontWeight: 600, color: 'var(--mom-primary-dark, var(--mom-primary))' }}>@{m[1]}</span>{text.slice(m[0].length - 1)}</p>;
}

// ── Send button + text input with the no-links rule ──
function Composer({ value, onChange, onSubmit, onCancel, placeholder, small, blockLinks, copy, autoFocus, busy, inputRef }) {
    const [focused, setFocused] = useState(false);
    const [shake, setShake] = useState(0);
    const linked = blockLinks && hasLink(value);
    const canSend = !!value.trim() && !linked && !busy;
    const submit = () => {
        if (!canSend) { if (linked) setShake((n) => n + 1); return; }
        onSubmit();
    };
    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <div key={shake} style={{ position: 'relative', animation: shake ? 'momShake 0.32s' : 'none' }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    autoFocus={autoFocus}
                    onChange={(e) => onChange(e.target.value.slice(0, MAX))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
                        if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); }
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    placeholder={placeholder}
                    maxLength={MAX}
                    aria-invalid={linked || undefined}
                    style={{
                        width: '100%', padding: small ? '8px 40px 8px 14px' : '10px 44px 10px 16px',
                        background: 'var(--mom-cream)', border: 'none', borderRadius: small ? 10 : 12,
                        fontFamily: SANS, fontSize: small ? 13 : 14, color: 'var(--mom-charcoal)',
                        outline: 'none', boxSizing: 'border-box',
                        boxShadow: linked ? '0 0 0 2px color-mix(in srgb, var(--mom-primary) 45%, transparent)'
                            : focused ? '0 0 0 2px color-mix(in srgb, var(--mom-primary) 25%, transparent)' : 'none',
                        transition: 'box-shadow 0.15s ease',
                    }}
                />
                {value.trim() && (
                    <button
                        onClick={submit}
                        aria-disabled={!canSend}
                        aria-label="Post"
                        style={{
                            position: 'absolute', right: small ? 4 : 6, top: '50%', transform: 'translateY(-50%)',
                            width: small ? 30 : 32, height: small ? 30 : 32, borderRadius: '50%',
                            background: 'var(--mom-primary)', border: 'none', cursor: canSend ? 'pointer' : 'not-allowed',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: canSend ? 1 : 0.3, transition: 'opacity 0.2s ease',
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                    </button>
                )}
            </div>
            <div aria-live="polite">
                {linked && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 7, fontFamily: SANS, fontSize: 12, color: 'var(--mom-primary-dark, var(--mom-primary))', animation: 'fadeUp 0.25s ease' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" /></svg>
                        {copy.commentLinkBlocked}
                    </div>
                )}
                {!linked && value.length >= 240 && (
                    <div style={{ fontFamily: SANS, fontSize: 11, color: FAINT, textAlign: 'right', marginTop: 4 }}>{MAX - value.length} left</div>
                )}
            </div>
        </div>
    );
}

// ── Anchored menu (⋯), portalled ──
function RowMenu({ anchor, items, onClose }) {
    const ref = useRef(null);
    const [pos, setPos] = useState(null);
    useEffect(() => {
        if (!anchor) return undefined;
        const r = anchor.getBoundingClientRect();
        const h = 52 * items.length + 12;
        const below = r.bottom + h + 8 < window.innerHeight;
        setPos({ left: Math.max(8, Math.min(window.innerWidth - 176, r.right - 168)), top: below ? r.bottom + 4 : r.top - h - 4 });
        const close = (e) => { if (ref.current && !ref.current.contains(e.target) && e.target !== anchor && !anchor.contains(e.target)) onClose(); };
        const esc = (e) => { if (e.key === 'Escape') { onClose(); anchor.focus(); } };
        document.addEventListener('pointerdown', close, true);
        document.addEventListener('keydown', esc);
        window.addEventListener('scroll', onClose, true);
        window.addEventListener('resize', onClose);
        return () => {
            document.removeEventListener('pointerdown', close, true);
            document.removeEventListener('keydown', esc);
            window.removeEventListener('scroll', onClose, true);
            window.removeEventListener('resize', onClose);
        };
    }, [anchor, items.length, onClose]);
    if (!pos) return null;
    return createPortal(
        <div ref={ref} role="menu" onClick={stop} onPointerDown={stop}
            onKeyDown={(e) => {
                if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
                e.preventDefault();
                const items = Array.from(ref.current.querySelectorAll('[role="menuitem"]'));
                const i = items.indexOf(document.activeElement);
                const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1
                    : (i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
                if (items[next]) items[next].focus();
            }}
            style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 3200, minWidth: 168, background: '#fff', borderRadius: 14, boxShadow: '0 14px 40px rgba(62,47,47,.2)', padding: 6, animation: 'modalIn 0.14s ease-out' }}>
            {items.map((it, i) => (
                <MenuItem key={it.label} item={it} autoFocus={i === 0} onDone={onClose} />
            ))}
        </div>,
        document.body
    );
}
function MenuItem({ item, autoFocus, onDone }) {
    const [hover, setHover] = useState(false);
    return (
        <button role="menuitem" autoFocus={autoFocus}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            onClick={() => { onDone(); item.onSelect(); }}
            style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, border: 0, background: hover ? 'var(--mom-cream)' : 'none', padding: '11px 12px', borderRadius: 9, fontFamily: SANS, fontSize: 14, color: 'var(--mom-charcoal)', textAlign: 'left', cursor: 'pointer', minHeight: 40 }}>
            {item.label}
        </button>
    );
}

// ── Bottom sheet (phone) / centred card (desktop), portalled ──
function Sheet({ title, sub, children, onClose }) {
    useEffect(() => {
        const esc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', esc);
        return () => document.removeEventListener('keydown', esc);
    }, [onClose]);
    const wide = typeof window !== 'undefined' && window.innerWidth > 600;
    return createPortal(
        <div onClick={(e) => { stop(e); onClose(); }} onPointerDown={stop}
            style={{ position: 'fixed', inset: 0, zIndex: 3150, background: 'rgba(43,34,34,.32)', display: 'flex', alignItems: wide ? 'center' : 'flex-end', justifyContent: 'center', animation: 'fadeIn 0.2s ease' }}>
            <div role="dialog" aria-label={title} onClick={stop}
                style={{ width: wide ? 400 : '100%', background: '#fff', borderRadius: wide ? 22 : '24px 24px 0 0', padding: wide ? '24px 24px 22px' : '10px 22px 28px', boxShadow: '0 20px 60px rgba(62,47,47,.25)', animation: wide ? 'modalIn 0.18s ease-out' : 'slideUp 0.26s cubic-bezier(.2,.8,.2,1)' }}>
                {!wide && <div style={{ width: 38, height: 4, borderRadius: 2, background: HAIR, margin: '0 auto 16px' }} />}
                <h4 style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 600, fontSize: 19, margin: 0, color: 'var(--mom-charcoal)' }}>{title}</h4>
                {sub && <p style={{ fontFamily: SANS, fontSize: 13, color: SOFT, margin: '4px 0 16px' }}>{sub}</p>}
                {children}
            </div>
        </div>,
        document.body
    );
}
const btn = (primary) => ({
    flex: 1, border: 0, borderRadius: 99, padding: 13, fontFamily: SANS, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 44,
    ...(primary
        ? { background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: '#fff', boxShadow: '0 6px 18px color-mix(in srgb, var(--mom-primary) 30%, transparent)' }
        : { background: 'var(--mom-cream)', color: 'var(--mom-charcoal)' }),
});

function ReportSheet({ copy, onCancel, onReport }) {
    const [pick, setPick] = useState(null);
    const reasons = [['unkind', copy.commentReportReason1], ['spam', copy.commentReportReason2], ['other', copy.commentReportReason3]];
    return (
        <Sheet title={copy.commentReportTitle} sub={copy.commentReportSubtext} onClose={onCancel}>
            <div role="radiogroup">
                {reasons.map(([key, label]) => {
                    const on = pick === key;
                    return (
                        <button key={key} role="radio" aria-checked={on} onClick={() => setPick(key)}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', border: `1px solid ${on ? 'var(--mom-primary)' : HAIR}`, background: on ? 'color-mix(in srgb, var(--mom-primary) 7%, #fff)' : '#fff', borderRadius: 14, padding: 14, marginBottom: 9, fontFamily: SANS, fontSize: 14, color: 'var(--mom-charcoal)', textAlign: 'left', cursor: 'pointer' }}>
                            <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${on ? 'var(--mom-primary)' : 'color-mix(in srgb, var(--mom-charcoal) 30%, #fff)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {on && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--mom-primary)' }} />}
                            </span>
                            {label}
                        </button>
                    );
                })}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button style={btn(false)} onClick={onCancel}>Cancel</button>
                <button style={{ ...btn(true), opacity: pick ? 1 : 0.4, cursor: pick ? 'pointer' : 'not-allowed' }} disabled={!pick} onClick={() => onReport(pick)}>Report</button>
            </div>
        </Sheet>
    );
}

function Toast({ message, action, onAction }) {
    return createPortal(
        <div role="status" onClick={stop} onPointerDown={stop}
            style={{ position: 'fixed', left: '50%', bottom: 26, transform: 'translateX(-50%)', zIndex: 3300, background: 'var(--mom-charcoal)', color: '#fff', fontFamily: SANS, fontSize: 13, padding: '11px 18px', borderRadius: 99, display: 'flex', gap: 16, alignItems: 'center', boxShadow: '0 10px 30px rgba(0,0,0,.2)', animation: 'fadeUp 0.2s ease' }}>
            <span>{message}</span>
            {action && <button onClick={onAction} style={{ border: 0, background: 'none', color: 'var(--mom-primary-light, #f0d6d4)', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0 }}>{action}</button>}
        </div>,
        document.body
    );
}

// ── One comment row ──
function CommentRow({
    comment, isReply, isLast, viewer, photoFor, highlighted, copy, editing,
    onReply, onEditStart, editProps, onMenu, onAdminDelete, deleting, adminReviewHref,
}) {
    const c = comment;
    const mine = !!viewer.userId && c.userId === viewer.userId;
    const heldForAdmin = c.held === true && viewer.isAdmin;
    const heldForMe = c.held === true && mine;
    const [hoverAct, setHoverAct] = useState(null);
    const act = (key) => ({
        background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', minHeight: 32,
        fontFamily: SANS, fontSize: 11.5, fontWeight: 600, color: hoverAct === key ? 'var(--mom-charcoal)' : SOFT,
    });
    const canReply = !!viewer.userId && !isTemp(c) && c.held !== true && (viewer.repliesOn || (viewer.isAdmin && !isReply));
    const canEdit = mine && !isTemp(c) && c.held !== true && c.status === 'active' && !editing;
    const showMenu = !viewer.isAdmin && !!viewer.userId && !isTemp(c) && !c.isCoach;
    return (
        <div data-comment-id={c.id}
            style={{
                display: 'flex', gap: isReply ? 8 : 10, alignItems: 'flex-start',
                padding: isReply ? '9px 0' : '14px 0', paddingLeft: isReply ? 42 : 0,
                borderBottom: isLast ? 'none' : (isReply ? 'none' : `1px solid ${HAIR}`),
                animation: isTemp(c) || c._fresh ? 'fadeIn 0.3s ease-out' : 'none',
                background: highlighted ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)' : 'transparent',
                borderRadius: highlighted || heldForAdmin ? 10 : 0,
                outline: heldForAdmin ? '1.5px dashed color-mix(in srgb, var(--mom-primary) 45%, transparent)' : 'none',
                outlineOffset: heldForAdmin ? 2 : 0,
                transition: 'background 0.5s ease',
            }}>
            <MemberCardTrigger userId={c.isCoach ? null : c.userId} name={c.userName || 'Member'} display="block">
                <div style={{ width: isReply ? 24 : 32, height: isReply ? 24 : 32, borderRadius: '50%', flexShrink: 0, background: c.isCoach ? 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))' : 'var(--mom-cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <span style={{ fontFamily: SANS, fontSize: isReply ? 10 : 12, fontWeight: 700, color: c.isCoach ? '#fff' : 'var(--mom-charcoal)' }}>{c.isCoach ? 'D' : (c.userInitials || '?')}</span>
                    {!c.isCoach && <AvatarPhoto url={photoFor(c.userId)} />}
                </div>
            </MemberCardTrigger>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2, flexWrap: 'wrap' }}>
                    {isReply && <span style={{ fontFamily: SANS, fontSize: 11, color: FAINT }}>↳</span>}
                    <span style={{ fontFamily: SANS, fontSize: isReply ? 12 : 13, fontWeight: 600, color: c.isCoach ? 'var(--mom-primary)' : 'var(--mom-charcoal)' }}>{c.isCoach ? 'Danielle' : c.userName}</span>
                    {c.isCoach && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--mom-primary)', display: 'inline-block' }} />}
                    <span style={{ fontFamily: SANS, fontSize: 12, color: SOFT }}>{timeAgo(c.createdAt)}</span>
                    {c.editedAt && <span title={`edited ${timeAgo(c.editedAt)}`} style={{ fontFamily: SANS, fontSize: 11, color: FAINT, fontStyle: 'italic' }}>· edited</span>}
                    {heldForAdmin && (
                        <a href={adminReviewHref} onClick={stop} style={{ fontFamily: SANS, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--mom-primary-dark, var(--mom-primary))', background: 'color-mix(in srgb, var(--mom-primary) 9%, #fff)', borderRadius: 7, padding: '2px 8px', textDecoration: 'none' }}>Hidden · review</a>
                    )}
                </div>
                {editing ? editProps : <ReplyText text={c.text} isReply={isReply} />}
                {heldForMe && !editing && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontFamily: SANS, fontSize: 11.5, fontStyle: 'italic', color: SOFT }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                        {copy.commentHeldNote}
                    </div>
                )}
                {(canReply || canEdit) && (
                    <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                        {canReply && <button style={act('reply')} onMouseEnter={() => setHoverAct('reply')} onMouseLeave={() => setHoverAct(null)} onClick={() => onReply(c)}>{copy.commentReplyLabel}</button>}
                        {canEdit && <button style={act('edit')} onMouseEnter={() => setHoverAct('edit')} onMouseLeave={() => setHoverAct(null)} onClick={() => onEditStart(c)}>Edit</button>}
                    </div>
                )}
            </div>
            {showMenu && (
                <button aria-label="More" aria-haspopup="menu" onClick={(e) => onMenu(c, e.currentTarget)}
                    onMouseEnter={() => setHoverAct('menu')} onMouseLeave={() => setHoverAct(null)}
                    style={{ border: 0, background: hoverAct === 'menu' ? 'var(--mom-cream)' : 'none', width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: -4, cursor: 'pointer', opacity: hoverAct === 'menu' ? 1 : 0.6 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--mom-charcoal)"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
                </button>
            )}
            {viewer.isAdmin && !isTemp(c) && (
                <button onClick={() => onAdminDelete(c)} disabled={deleting} title="Delete comment"
                    onMouseEnter={() => setHoverAct('del')} onMouseLeave={() => setHoverAct(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', flexShrink: 0, opacity: deleting ? 0.3 : (hoverAct === 'del' ? 1 : 0.4) }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--mom-charcoal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                </button>
            )}
        </div>
    );
}

// ── The thread ──
export default function ClassCommentsV2({
    classId, recordingId, userId, userName, isAdmin, commentCount = 0, defaultExpanded = false, getToken, highlightCommentId = null, onFallback,
}) {
    const copy = useMemo(() => ({ ...DEFAULT_COPY, ...Object.fromEntries(Object.entries(getCachedSiteContent() || {}).filter(([k, v]) => k in DEFAULT_COPY && typeof v === 'string' && v.trim())) }), []);
    const viewer = useMemo(() => ({ userId, isAdmin: !!isAdmin, repliesOn: true }), [userId, isAdmin]);
    const userInitials = userName ? userName.split(' ').map((n) => n.charAt(0).toUpperCase()).slice(0, 2).join('') : '?';

    const threadKey = classId || recordingId || '';
    const [cached] = useState(() => readThreadCache(threadKey));
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [top, setTop] = useState(() => (cached ? cached.top : []));
    const [hasMore, setHasMore] = useState(false);
    const [limitCount, setLimitCount] = useState(isAdmin ? ADMIN_PAGE : PAGE);
    const [loading, setLoading] = useState(!cached);
    const [loadingMore, setLoadingMore] = useState(false);
    // 'cache' holds cached replies until the first live reply snapshot arrives.
    const [repliesByChunk, setRepliesByChunk] = useState(() => (cached ? { cache: cached.replies } : {}));
    const liveSeen = useRef(false);
    const [local, setLocal] = useState({});           // optimistic + just-saved docs by id
    const [hidden, setHidden] = useState(() => new Set()); // pending-delete (undo window)
    const [reported, setReported] = useState(() => new Set());
    const [threadsOpen, setThreadsOpen] = useState(() => new Set());
    const [replyTo, setReplyTo] = useState(null);     // { threadId, mention: {id, name} | null }
    const [drafts, setDrafts] = useState({});
    const [text, setText] = useState('');
    const [posting, setPosting] = useState(false);
    const [postError, setPostError] = useState(null);
    const [replyError, setReplyError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState(false);
    const [menu, setMenu] = useState(null);           // { comment, anchor }
    const [reportFor, setReportFor] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [toast, setToast] = useState(null);         // { message, action, onAction }
    const [deletingId, setDeletingId] = useState(null);
    const [highlightedId, setHighlightedId] = useState(null);
    const [postedFor, setPostedFor] = useState(null);
    const pendingDeletes = useRef(new Map());         // id → timer
    const containerRef = useRef(null);
    const highlightDone = useRef(null);
    const toastTimer = useRef(null);
    // Stable handle on the fallback (the wrapper passes a fresh arrow each render;
    // depending on it directly would resubscribe the thread on every render).
    const onFallbackRef = useRef(onFallback);
    onFallbackRef.current = onFallback;

    const showToast = useCallback((t, ms = 2400) => {
        clearTimeout(toastTimer.current);
        setToast(t);
        toastTimer.current = setTimeout(() => setToast(null), ms);
    }, []);

    // Reset per thread.
    useEffect(() => {
        setReplyTo(null); setDrafts({}); setLocal({}); setThreadsOpen(new Set()); setLimitCount(isAdmin ? ADMIN_PAGE : PAGE);
    }, [classId, recordingId, isAdmin]);

    // Load-time telemetry (UX §8 targets: ≤300ms cached, ≤1s cold). One span per
    // thread mount; ends at first paint — immediately when painted from cache.
    const spanRef = useRef(null);
    useEffect(() => {
        try {
            spanRef.current = Sentry.startInactiveSpan({ name: 'comment-thread.first-paint', op: 'ui.comments', attributes: { source: cached ? 'cache' : 'network' } });
            if (cached && spanRef.current) { spanRef.current.end(); spanRef.current = null; }
        } catch (_) { spanRef.current = null; }
        return () => { try { if (spanRef.current) spanRef.current.end(); } catch (_) { /* noop */ } };
    }, []);

    // Top-level listener. A failure (e.g. index missing) hands back to the legacy thread.
    useEffect(() => {
        if (!classId && !recordingId) return undefined;
        const unsub = subscribeTopLevel({
            classId, recordingId, limitCount,
            onUpdate: ({ comments, hasMore: more }) => {
                setTop(comments); setHasMore(more); setLoading(false); setLoadingMore(false);
                try { if (spanRef.current) { spanRef.current.end(); spanRef.current = null; } } catch (_) { /* noop */ }
            },
            onError: () => { if (typeof onFallbackRef.current === 'function') onFallbackRef.current(); },
        });
        return () => unsub();
    }, [classId, recordingId, limitCount]);

    // Reply listeners for the visible top-level comments (chunks of 30).
    const topIdsKey = top.map((c) => c.id).join(',');
    useEffect(() => {
        const ids = topIdsKey ? topIdsKey.split(',') : [];
        const unsubs = [];
        const keep = {};
        for (let i = 0; i < ids.length; i += 30) {
            const chunkKey = String(i / 30);
            keep[chunkKey] = true;
            unsubs.push(subscribeRepliesFor(ids.slice(i, i + 30), (docs) => setRepliesByChunk((prev) => {
                const next = { ...prev, [chunkKey]: docs };
                delete next.cache; // live data has arrived — drop the cached replies
                liveSeen.current = true;
                return next;
            })));
        }
        setRepliesByChunk((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => keep[k] || k === 'cache')));
        return () => unsubs.forEach((u) => u());
    }, [topIdsKey]);

    // Keep the session cache fresh once live data is in (never from cache alone).
    useEffect(() => {
        if (loading || !liveSeen.current) return;
        writeThreadCache(threadKey, top, Object.entries(repliesByChunk).filter(([k]) => k !== 'cache').flatMap(([, v]) => v));
    }, [loading, top, repliesByChunk, threadKey]);

    // This member's reports (cross-device), plus anything reported this session.
    useEffect(() => {
        if (!userId || isAdmin) return;
        let cancelled = false;
        fetchMyReportedIds(userId).then((ids) => { if (!cancelled && ids.length) setReported((prev) => new Set([...prev, ...ids])); });
        return () => { cancelled = true; };
    }, [userId, isAdmin]);

    // Flush pending deletes on unmount (no undo after leaving the page).
    useEffect(() => () => {
        pendingDeletes.current.forEach((entry) => { clearTimeout(entry.timer); entry.commit(); });
        clearTimeout(toastTimer.current);
    }, []);

    // ── Merge server + local, apply visibility ──
    const all = useMemo(() => {
        const server = [...top, ...Object.values(repliesByChunk).flat()];
        const byId = new Map(server.map((c) => [c.id, c]));
        Object.values(local).forEach((c) => { if (!byId.has(c.id)) byId.set(c.id, c); });
        return [...byId.values()];
    }, [top, repliesByChunk, local]);

    const visibleToMe = useCallback((c) => {
        if (c.status && c.status !== 'active') return false;
        if (hidden.has(c.id)) return false;
        if (c.held === true) return isAdmin || (!!userId && c.userId === userId);
        return true;
    }, [hidden, isAdmin, userId]);

    const topLevel = all.filter((c) => !c.parentCommentId && visibleToMe(c))
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    const repliesOf = {};
    all.forEach((c) => {
        if (!c.parentCommentId || !visibleToMe(c)) return;
        (repliesOf[c.parentCommentId] = repliesOf[c.parentCommentId] || []).push(c);
    });
    Object.values(repliesOf).forEach((list) => list.sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt))));

    const photoFor = useMemberPhotos([userId, ...all.map((c) => c.userId)]);

    // Count = what everyone can see (held/deleted excluded). Trust the server
    // count while loading or when more pages exist.
    const publicCount = all.filter((c) => (!c.status || c.status === 'active') && c.held !== true && !hidden.has(c.id)).length;
    const displayCount = loading ? commentCount : (hasMore ? Math.max(commentCount, publicCount) : publicCount);
    const previewComment = topLevel.find((c) => !c.isCoach && c.held !== true && !isTemp(c)) || null;

    // ── Deep link: open the thread, expand the right replies, ring the comment ──
    useEffect(() => { if (highlightCommentId) setExpanded(true); }, [highlightCommentId]);
    useEffect(() => {
        if (!highlightCommentId || loading || highlightDone.current === highlightCommentId) return undefined;
        const target = all.find((c) => c.id === highlightCommentId);
        if (!target) return undefined;
        highlightDone.current = highlightCommentId;
        if (target.parentCommentId) setThreadsOpen((prev) => new Set([...prev, target.parentCommentId]));
        const t1 = setTimeout(() => {
            const el = containerRef.current && containerRef.current.querySelector(`[data-comment-id="${highlightCommentId}"]`);
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedId(highlightCommentId);
        }, 150);
        const t2 = setTimeout(() => setHighlightedId(null), 2800);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [highlightCommentId, loading, all]);

    // ── Posting ──
    const memberPost = async ({ body, parentCommentId = null, replyToComment = null }) => {
        const tempId = `temp-${Date.now()}`;
        const temp = {
            id: tempId, classId: classId || null, recordingId: recordingId || null, parentCommentId,
            userId, userName: userName || 'Member', userInitials, isCoach: false, text: body.slice(0, MAX),
            createdAt: new Date().toISOString(), status: 'active',
        };
        setLocal((p) => ({ ...p, [tempId]: temp }));
        const r = await postMemberComment({ classId, recordingId, text: body, parentCommentId, replyToCommentId: replyToComment ? replyToComment.id : null }, getToken);
        setLocal((p) => {
            const n = { ...p };
            delete n[tempId];
            if (r && r.comment) n[r.comment.id] = { ...r.comment, _fresh: true };
            return n;
        });
        if (r && r.comment) {
            if (!parentCommentId && !r.held && typeof getToken === 'function') {
                notifyNewComment({ classId: classId || null, recordingId: recordingId || null, vodAssetId: recordingId || null, commentId: r.comment.id, commentText: r.comment.text, fromName: userName || 'A member' }, getToken);
            }
            return { ok: true, comment: r.comment };
        }
        return { ok: false, error: r && r.error };
    };

    const handlePostTop = async () => {
        const body = text.trim();
        if (!body || posting || !userId) return;
        if (!isAdmin && hasLink(body)) return;
        setPosting(true); setPostError(null); setText('');
        let ok;
        if (isAdmin) {
            const tempId = `temp-${Date.now()}`;
            const temp = { id: tempId, classId: classId || null, recordingId: recordingId || null, parentCommentId: null, userId, userName: userName || 'Member', userInitials, isCoach: true, text: body.slice(0, MAX), createdAt: new Date().toISOString(), status: 'active' };
            setLocal((p) => ({ ...p, [tempId]: temp }));
            const result = await postComment({ userId, userName: userName || 'Member', userInitials, isCoach: true, classId, recordingId, text: body });
            setLocal((p) => { const n = { ...p }; delete n[tempId]; if (result) n[result.id] = result; return n; });
            ok = !!result;
        } else {
            const r = await memberPost({ body });
            ok = r.ok;
            if (!ok && r.error === 'link') { setText(body); setPosting(false); return; }
        }
        if (!ok) { setText(body); setPostError('failed'); }
        setPosting(false);
    };

    const startReply = (c) => {
        const threadId = c.parentCommentId || c.id;
        const mention = c.parentCommentId ? { id: c.id, name: c.isCoach ? 'Danielle' : c.userName } : null;
        setReplyTo({ threadId, mention });
        setReplyError(null);
        setThreadsOpen((prev) => new Set([...prev, threadId]));
        setTimeout(() => {
            const el = containerRef.current && containerRef.current.querySelector(`[data-reply-for="${threadId}"]`);
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
    };

    const handlePostReply = async () => {
        if (!replyTo) return;
        const { threadId, mention } = replyTo;
        const draft = (drafts[threadId] || '').trim();
        if (!draft || !userId) return;
        if (!isAdmin && hasLink(draft)) return;
        const body = mention ? `@${mention.name} ${draft}` : draft;
        setDrafts((p) => ({ ...p, [threadId]: '' }));
        setReplyError(null);
        const parent = all.find((c) => c.id === threadId);
        if (isAdmin) {
            // Danielle's existing reply path, byte-for-byte in behaviour.
            const tempId = `temp-reply-${Date.now()}`;
            setLocal((p) => ({ ...p, [tempId]: { id: tempId, classId: classId || null, recordingId: recordingId || null, parentCommentId: threadId, userId, userName: userName || 'Member', userInitials, isCoach: true, text: body.slice(0, MAX), createdAt: new Date().toISOString(), status: 'active' } }));
            const result = await postComment({ userId, userName: userName || 'Member', userInitials, isCoach: true, classId, recordingId, text: body, parentCommentId: threadId });
            setLocal((p) => { const n = { ...p }; delete n[tempId]; if (result) n[result.id] = result; return n; });
            if (result) {
                markDraftAnswered(threadId);
                const recipient = mention && mention.id ? all.find((c) => c.id === mention.id) : parent;
                if (recipient && recipient.userId && recipient.userId !== userId && typeof getToken === 'function') {
                    notifyCoachReply({ recipientUserId: recipient.userId, classId: classId || null, recordingId: recordingId || null, vodAssetId: recordingId || null, parentCommentId: threadId, parentText: recipient.text || '', replyCommentId: result.id, replyText: result.text || body }, getToken);
                }
                setPostedFor(threadId); setTimeout(() => setPostedFor(null), 2000);
            } else { setDrafts((p) => ({ ...p, [threadId]: draft })); setReplyError('failed'); }
            return;
        }
        const r = await memberPost({ body, parentCommentId: threadId, replyToComment: mention && mention.id ? { id: mention.id } : null });
        if (r.ok) {
            setReplyTo(null);
            setPostedFor(threadId); setTimeout(() => setPostedFor(null), 2000);
        } else if (r.error === 'parent-missing' || r.error === 'parent-unavailable') {
            setDrafts((p) => ({ ...p, [threadId]: draft })); setReplyError('parent-gone');
        } else {
            setDrafts((p) => ({ ...p, [threadId]: draft })); setReplyError(r.error === 'link' ? null : 'failed');
        }
    };

    const repostAsTopLevel = () => {
        if (!replyTo) return;
        setText((drafts[replyTo.threadId] || '').trim());
        setReplyTo(null); setReplyError(null);
    };

    // ── Member delete with Undo (server call after the window closes) ──
    const scheduleDelete = (c) => {
        setHidden((prev) => new Set([...prev, c.id]));
        let committed = false;
        const commit = () => {
            if (committed) return; committed = true;
            pendingDeletes.current.delete(c.id);
            deleteOwnComment(c.id, getToken).then((ok) => {
                if (!ok) { setHidden((prev) => { const n = new Set(prev); n.delete(c.id); return n; }); showToast({ message: "Couldn't delete — please try again." }); }
            });
        };
        const timer = setTimeout(commit, UNDO_MS);
        pendingDeletes.current.set(c.id, { timer, commit });
        showToast({
            message: copy.commentDeletedUndo,
            action: 'Undo',
            onAction: () => {
                const entry = pendingDeletes.current.get(c.id);
                if (entry) { clearTimeout(entry.timer); pendingDeletes.current.delete(c.id); }
                setHidden((prev) => { const n = new Set(prev); n.delete(c.id); return n; });
                setToast(null);
            },
        }, UNDO_MS);
    };
    const requestDelete = (c) => {
        const othersReplies = !c.parentCommentId ? (repliesOf[c.id] || []).filter((r) => r.userId !== userId).length : 0;
        if (othersReplies > 0) setConfirmDelete({ comment: c, count: othersReplies });
        else scheduleDelete(c);
    };

    const handleReport = async (c, reason) => {
        setReportFor(null);
        setReported((prev) => new Set([...prev, c.id]));
        showToast({ message: copy.commentReportThanks });
        reportComment(c.id, reason, getToken);
    };

    const openMenu = (c, anchor) => {
        const mine = c.userId === userId;
        setMenu({ comment: c, anchor, items: mine
            ? [{ label: 'Delete', onSelect: () => requestDelete(c) }]
            : [{ label: 'Report', onSelect: () => setReportFor(c) }] });
    };
    const closeMenu = useCallback(() => setMenu(null), []);

    // ── Admin delete (unchanged behaviour: confirm, cascade, notification cleanup) ──
    const handleAdminDelete = async (c) => {
        const cascade = !c.parentCommentId ? (repliesOf[c.id] || []).filter((r) => !isTemp(r)) : [];
        const msg = cascade.length ? `Delete this comment and ${cascade.length} repl${cascade.length === 1 ? 'y' : 'ies'}?` : 'Delete this comment?';
        if (!window.confirm(msg)) return;
        setDeletingId(c.id);
        const ok = await deleteComment(c.id, classId, recordingId);
        if (ok) {
            setHidden((prev) => new Set([...prev, c.id, ...cascade.map((r) => r.id)]));
            if (typeof getToken === 'function') {
                const ids = c.parentCommentId ? [c.id] : [];
                cascade.forEach((r) => ids.push(r.id));
                ids.forEach((rid) => removeCoachReplyNotification(rid, getToken));
            }
            if (replyTo && replyTo.threadId === c.id) setReplyTo(null);
        }
        setDeletingId(null);
    };

    // ── Edit (author; links blocked for members) ──
    const startEdit = (c) => { setEditingId(c.id); setEditText(c.text || ''); setEditError(false); };
    const cancelEdit = () => { setEditingId(null); setEditText(''); setEditError(false); };
    const saveEdit = async (c) => {
        const trimmed = editText.trim().slice(0, MAX);
        if (editSaving) return;
        if (!trimmed || trimmed === (c.text || '').trim()) { cancelEdit(); return; }
        if (!isAdmin && hasLink(trimmed)) return;
        setEditSaving(true); setEditError(false);
        const original = c.text;
        setLocal((p) => ({ ...p, [c.id]: { ...c, text: trimmed, editedAt: new Date().toISOString() } }));
        const ok = await editComment(c.id, trimmed);
        if (ok) cancelEdit();
        else { setLocal((p) => ({ ...p, [c.id]: { ...c, text: original } })); setEditError(true); }
        setEditSaving(false);
    };
    const editor = (c, isReply) => (
        <div>
            <Composer value={editText} onChange={(v) => { setEditText(v); setEditError(false); }} onSubmit={() => saveEdit(c)} onCancel={cancelEdit}
                small={isReply} blockLinks={!isAdmin} copy={copy} autoFocus busy={editSaving} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                <button onClick={cancelEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: SANS, fontSize: 11, fontWeight: 600, color: SOFT }}>Cancel</button>
                <span style={{ fontFamily: SANS, fontSize: 11, color: FAINT }}>Enter to save · Esc to cancel</span>
            </div>
            {editError && <p style={{ fontFamily: SANS, fontSize: 11, color: '#b4232a', marginTop: 6, marginBottom: 0 }}>Couldn't save — please try again.</p>}
        </div>
    );

    const handleLoadMore = () => { if (!hasMore || loadingMore) return; setLoadingMore(true); setLimitCount((n) => n + PAGE); };

    // ── Render helpers (functions, not components) ──
    const renderRow = (c, isReply, isLast) => {
        if (!isAdmin && reported.has(c.id) && c.userId !== userId) {
            return (
                <div key={c.id} style={{ padding: isReply ? '9px 0 9px 42px' : '12px 0', fontFamily: SANS, fontSize: 12.5, fontStyle: 'italic', color: FAINT, borderBottom: isLast || isReply ? 'none' : `1px solid ${HAIR}` }}>
                    {copy.commentReportedPlaceholder}
                </div>
            );
        }
        return (
            <CommentRow key={c.id} comment={c} isReply={isReply} isLast={isLast} viewer={viewer} photoFor={photoFor}
                highlighted={highlightedId === c.id} copy={copy} editing={editingId === c.id}
                onReply={startReply} onEditStart={startEdit} editProps={editingId === c.id ? editor(c, isReply) : null}
                onMenu={openMenu} onAdminDelete={handleAdminDelete} deleting={deletingId === c.id}
                adminReviewHref="/admin?tab=moderation" />
        );
    };

    const renderReplyComposer = (parent) => {
        const threadId = parent.id;
        const to = replyTo.mention ? replyTo.mention.name : (parent.isCoach ? 'Danielle' : parent.userName);
        return (
            <div key={`compose-${threadId}`} data-reply-for={threadId} style={{ padding: '4px 0 12px 42px', animation: 'fadeUp 0.22s ease' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: SANS, fontSize: 11.5, color: SOFT, marginBottom: 6 }}>
                    <span>{fill(copy.commentReplyingTo, { name: '' })}<b style={{ color: 'var(--mom-charcoal)' }}>{to}</b></span>
                    <button aria-label="Cancel reply" onClick={() => { setReplyTo(null); setReplyError(null); }} style={{ border: 0, background: 'none', fontSize: 16, color: FAINT, padding: '2px 6px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>
                {replyTo.mention && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--mom-primary-light, #f0d6d4)', color: 'var(--mom-primary-dark, var(--mom-primary))', fontFamily: SANS, fontWeight: 600, fontSize: 12.5, borderRadius: 8, padding: '2px 4px 2px 8px', margin: '0 6px 6px 0' }}>
                        @{replyTo.mention.name}
                        <button aria-label="Remove mention" onClick={() => setReplyTo((r) => ({ ...r, mention: null }))} style={{ border: 0, background: 'none', color: 'inherit', fontSize: 13, padding: '0 3px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </span>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, marginTop: 4, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        <span style={{ fontFamily: SANS, fontSize: 10, fontWeight: 700, color: '#fff' }}>{isAdmin ? 'D' : userInitials}</span>
                        {!isAdmin && <AvatarPhoto url={photoFor(userId)} />}
                    </div>
                    <Composer value={drafts[threadId] || ''} onChange={(v) => { setDrafts((p) => ({ ...p, [threadId]: v })); setReplyError(null); }}
                        onSubmit={handlePostReply} onCancel={() => setReplyTo(null)} placeholder={copy.commentReplyPlaceholder}
                        small blockLinks={!isAdmin} copy={copy} autoFocus />
                </div>
                {replyError === 'failed' && <p style={{ fontFamily: SANS, fontSize: 11.5, color: '#b4232a', margin: '6px 0 0 32px' }}>Didn't post — please try again.</p>}
                {replyError === 'parent-gone' && (
                    <p style={{ fontFamily: SANS, fontSize: 11.5, color: SOFT, margin: '6px 0 0 32px' }}>
                        That comment was removed. <button onClick={repostAsTopLevel} style={{ border: 0, background: 'none', padding: 0, fontFamily: SANS, fontSize: 11.5, fontWeight: 700, color: 'var(--mom-primary)', cursor: 'pointer' }}>Post as a new comment</button>
                    </p>
                )}
            </div>
        );
    };

    const renderThread = (parent, parentIdx) => {
        const replies = repliesOf[parent.id] || [];
        const open = threadsOpen.has(parent.id);
        let shown = replies;
        let hiddenCount = 0;
        if (!open && replies.length > SHOW_REPLIES) {
            const first = replies.slice(0, SHOW_REPLIES);
            // Danielle's replies and your own fresh replies are always in view.
            const pinned = replies.slice(SHOW_REPLIES).filter((r) => r.isCoach || isTemp(r) || r._fresh || r.id === highlightCommentId);
            shown = [...first, ...pinned];
            hiddenCount = replies.length - shown.length;
        }
        const composing = replyTo && replyTo.threadId === parent.id;
        const isLastThread = parentIdx === topLevel.length - 1;
        return (
            <div key={parent.id} style={{ borderBottom: isLastThread ? 'none' : `1px solid ${HAIR}` }}>
                {renderRow(parent, false, true)}
                {shown.map((r) => renderRow(r, true, true))}
                {hiddenCount > 0 && (
                    <ThreadToggle label={fill(copy.commentViewMoreReplies, { count: hiddenCount }).replace('1 more replies', '1 more reply')}
                        onClick={() => setThreadsOpen((prev) => new Set([...prev, parent.id]))} />
                )}
                {open && replies.length > SHOW_REPLIES && (
                    <ThreadToggle label={copy.commentHideReplies} onClick={() => setThreadsOpen((prev) => { const n = new Set(prev); n.delete(parent.id); return n; })} />
                )}
                {postedFor === parent.id && (
                    <div style={{ marginLeft: 42, marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'color-mix(in srgb, var(--mom-success, #6b9b7a) 14%, #fff)', color: '#4f7a5c', fontFamily: SANS, fontSize: 11, fontWeight: 600, borderRadius: 10, padding: '3px 9px', animation: 'fadeUp 0.2s ease' }}>✓ Posted</div>
                )}
                {composing && renderReplyComposer(parent)}
            </div>
        );
    };

    const commentIcon = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={SOFT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
    );
    const sk = (extra) => ({ background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.04) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s ease infinite', borderRadius: 8, ...extra });

    return (
        <div style={{ marginTop: 20 }}>
            <button onClick={() => setExpanded(!expanded)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', border: '1px solid rgba(255,255,255,0.55)', borderRadius: expanded ? '16px 16px 0 0' : 16, cursor: 'pointer', transition: 'border-radius 0.2s ease', boxShadow: expanded ? 'none' : 'var(--shadow-sm)', textAlign: 'left' }}>
                {commentIcon}
                <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: 'var(--mom-charcoal)' }}>
                    {displayCount > 0 ? `${displayCount} comment${displayCount !== 1 ? 's' : ''}` : 'Comments'}
                </span>
                {!expanded && previewComment && (
                    <span style={{ fontFamily: SANS, fontSize: 13, color: SOFT, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        &middot; "{previewComment.text.slice(0, 60)}{previewComment.text.length > 60 ? '...' : ''}" — {previewComment.userName}
                    </span>
                )}
                <span style={{ fontSize: 14, color: SOFT, transition: 'transform 0.2s ease', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0, marginLeft: 'auto' }}>▾</span>
            </button>

            {expanded && (
                <div ref={containerRef} style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', border: '1px solid rgba(255,255,255,0.55)', borderTop: 'none', borderRadius: '0 0 16px 16px', padding: '4px 20px 20px', boxShadow: 'var(--shadow-sm)', animation: 'fadeIn 0.2s ease-out' }}>
                    {loading ? (
                        <div aria-busy="true" aria-label="Loading comments" style={{ padding: '12px 0 4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12, marginBottom: 12, borderBottom: `1px solid ${HAIR}` }}>
                                <div style={sk({ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 })} />
                                <div style={sk({ flex: 1, height: 38, borderRadius: 12 })} />
                            </div>
                            {[0, 1, 2].map((i) => (
                                <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0' }}>
                                    <div style={sk({ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 })} />
                                    <div style={{ flex: 1 }}>
                                        <div style={sk({ width: 110, height: 11, borderRadius: 4, marginBottom: 8 })} />
                                        <div style={sk({ height: 11, borderRadius: 4, width: ['85%', '70%', '90%'][i] })} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            {userId && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 0 12px', borderBottom: topLevel.length ? `1px solid ${HAIR}` : 'none' }}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, marginTop: 3, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                        <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 700, color: '#fff' }}>{userInitials}</span>
                                        {!isAdmin && <AvatarPhoto url={photoFor(userId)} />}
                                    </div>
                                    <Composer value={text} onChange={(v) => { setText(v); setPostError(null); }} onSubmit={handlePostTop}
                                        placeholder="Say something..." blockLinks={!isAdmin} copy={copy} busy={posting} />
                                </div>
                            )}
                            {postError && <p style={{ fontFamily: SANS, fontSize: 12, color: '#b4232a', marginTop: 8, marginBottom: 0 }}>Didn't post — please try again.</p>}

                            {topLevel.length > 0 ? (
                                <div style={{ marginTop: userId ? 0 : 12 }}>
                                    {topLevel.map(renderThread)}
                                    {hasMore && (
                                        <button onClick={handleLoadMore} disabled={loadingMore}
                                            style={{ width: '100%', padding: '10px 0', marginTop: 8, background: 'none', border: 'none', cursor: 'pointer', fontFamily: SANS, fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)', opacity: loadingMore ? 0.5 : 1 }}>
                                            {loadingMore ? 'Loading...' : 'Load more comments'}
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0 8px' }}>
                                    <p style={{ fontFamily: SERIF, fontSize: 15, fontStyle: 'italic', color: SOFT }}>{userId ? 'Be the first to share your thoughts!' : 'No comments yet.'}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {menu && <RowMenu anchor={menu.anchor} items={menu.items} onClose={closeMenu} />}
            {reportFor && <ReportSheet copy={copy} onCancel={() => setReportFor(null)} onReport={(reason) => handleReport(reportFor, reason)} />}
            {confirmDelete && (
                <Sheet title="Delete your comment?" sub={fill(copy.commentDeleteWithReplies, { count: confirmDelete.count })} onClose={() => setConfirmDelete(null)}>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button style={btn(false)} onClick={() => setConfirmDelete(null)}>Keep it</button>
                        <button style={btn(true)} onClick={() => { const c = confirmDelete.comment; setConfirmDelete(null); scheduleDelete(c); }}>Delete</button>
                    </div>
                </Sheet>
            )}
            {toast && <Toast message={toast.message} action={toast.action} onAction={toast.onAction} />}
        </div>
    );
}

function ThreadToggle({ label, onClick }) {
    const [hover, setHover] = useState(false);
    return (
        <button onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, border: 0, background: 'none', cursor: 'pointer', padding: '6px 0 10px 42px', minHeight: 32, fontFamily: SANS, fontSize: 12, fontWeight: 600, color: hover ? 'var(--mom-charcoal)' : SOFT }}>
            <span style={{ width: 22, height: 1, background: 'color-mix(in srgb, var(--mom-charcoal) 25%, #fff)' }} />
            {label}
        </button>
    );
}
